import {
  Button,
  Empty,
  MenuProps,
  Spin,
  Tooltip,
  Typography,
  message,
} from "antd";
import React, { useEffect } from "react";
import {
  EllipsisOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CalculatorOutlined,
  //   EditOutlined,
  //   FileSearchOutlined,
  ExportOutlined,
  CopyOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { RankingProjectCardMenuKeys } from "modules/organization/models/enums";
import { filterKebabMenuItems } from "modules/organization/utility/commonFunctions";
import { useRbac } from "auth/rbac/rbac";
import CustomDropdownMenu from "modules/shared/components/CustomDropdownMenu";
import CustomError from "modules/shared/components/CustomError";
import { getAvatar } from "modules/shared/utility";
import { useNavigate } from "react-router-dom";
import { useOrganizationRoutes } from "routes/organizationRoute";
import { useProjectDetailsStore } from "modules/organization/store";
import { useRankingProjectListStore } from "modules/organization/store/rankingProjectListStore";
import { IRankingProjectCard } from "modules/organization/models/interface";
import { dateFormat } from "config/commonConfig";
import { mnDate } from "modules/shared/services";

const RankingProjectCard: React.FC<{
  rankingProject: IRankingProjectCard;
  onRun: (rankingProjectId: number) => void;
}> = ({ rankingProject, onRun }) => {
  const { hasPermissions } = useRbac();
  const navigate = useNavigate();
  const { organizationBaseRouteUrl } = useOrganizationRoutes();
  const { projectDetails } = useProjectDetailsStore();

  const _menuItems = [
    {
      label: "Equation Editor",
      key: RankingProjectCardMenuKeys.EQUATION_EDITOR,
      icon: <CalculatorOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    // {
    //     label: 'Edit Ranking Project',
    //     key: RankingProjectCardMenuKeys.EDIT_RANKING_PROJECT,
    //     icon: <EditOutlined role='img' aria-hidden />,
    //     allowedPermissions: [],
    // },
    // {
    //     label: 'Display Snapshot Result',
    //     key: RankingProjectCardMenuKeys.DISPLAY_SNAPSHOT_RESULT,
    //     icon: <FileSearchOutlined role='img' aria-hidden />,
    //     allowedPermissions: [],
    // },
    {
      label: "Export Ranks into Project",
      key: RankingProjectCardMenuKeys.EXPORT_RANKS,
      icon: <ExportOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    {
      label: "Copy",
      key: RankingProjectCardMenuKeys.COPY,
      icon: <CopyOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    {
      label: "Delete",
      key: RankingProjectCardMenuKeys.DELETE,
      icon: <DeleteOutlined role="img" aria-hidden />,
      danger: true,
      allowedPermissions: [],
    },
  ];

  // Equation Editor is the only destination that actually exists today. Edit Ranking Project and
  // Display Snapshot Result navigate to their documented (proposed) routes, which safely resolve to the
  // app's catch-all Not Found page until their own, separate stories build the real screens — this
  // matches the "correctly initiate/open, not fully function" scope for this menu (spec.md FR-009).
  // Export Ranks / Copy / Delete have no backend action to call yet at all, so they surface a clear
  // "not yet available" notice instead of hitting a nonexistent endpoint.
  const onSelect = (e: any, rankingProjectId: number) => {
    const key = parseInt(e.key);
    const basePath =
      organizationBaseRouteUrl +
      `/project/${projectDetails?.slug}/ranking-projects/${rankingProjectId}`;
    if (key === RankingProjectCardMenuKeys.EQUATION_EDITOR) {
      navigate(basePath + "/equation-editor");
    } else if (key === RankingProjectCardMenuKeys.EDIT_RANKING_PROJECT) {
      navigate(basePath + "/edit");
    } else if (key === RankingProjectCardMenuKeys.DISPLAY_SNAPSHOT_RESULT) {
      navigate(basePath + "/snapshot");
    } else if (key === RankingProjectCardMenuKeys.EXPORT_RANKS) {
      message.info("Export Ranks into Project is not yet available.");
    } else if (key === RankingProjectCardMenuKeys.COPY) {
      message.info("Copy is not yet available.");
    } else if (key === RankingProjectCardMenuKeys.DELETE) {
      message.info("Delete is not yet available.");
    }
  };

  const listActionItemsList: MenuProps["items"] = filterKebabMenuItems(
    _menuItems,
    hasPermissions,
    {
      project_role_id: projectDetails?.project_role_id,
    },
  );

  const cardTypeClassName =
    rankingProject.ranking_type_id === 1 ? "templateTypeCard" : "pointTypeCard";
  const cardClassName = rankingProject.is_running
    ? `activeCard ${cardTypeClassName}`
    : cardTypeClassName;

  return (
    <li className={cardClassName} key={rankingProject.id}>
      <div className="cardHead">
        <h3>
          <Typography.Text ellipsis={{ tooltip: rankingProject.title }}>
            {rankingProject.title}
          </Typography.Text>
        </h3>
        {rankingProject.created_by_user && (
          <Tooltip
            title={rankingProject.created_by_user.full_name}
            trigger={["hover", "focus"]}
          >
            <span className="userAvatar">
              {getAvatar(
                rankingProject.created_by_user.full_name,
                rankingProject.created_by_user.avatar_url,
              )}
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={rankingProject.is_running ? "Running" : "Run Ranking"}
          trigger={["hover", "focus"]}
        >
          <Button
            className="iconOnlyBtn smallIcon"
            aria-label={rankingProject.is_running ? "Running" : "Run Ranking"}
            onClick={() => onRun(rankingProject.id)}
          >
            {rankingProject.is_running ? (
              <PauseCircleOutlined role="img" aria-hidden />
            ) : (
              <PlayCircleOutlined role="img" aria-hidden />
            )}
          </Button>
        </Tooltip>
        <CustomDropdownMenu
          menuItems={listActionItemsList}
          onSelect={(e) => onSelect(e, rankingProject.id)}
          disabled={!listActionItemsList.some((item) => item?.key)}
        >
          <EllipsisOutlined role="img" aria-label="More" />
        </CustomDropdownMenu>
      </div>
      <div className="cardContent">
        <ul className="rankProjectStatsList">
          <li>
            <label>Last run date</label>
            <span>
              {rankingProject.last_execution
                ? mnDate(rankingProject.last_execution.executed_at).format(
                    dateFormat.tableDate,
                  )
                : "Not yet run"}
            </span>
          </li>
          {/* Common template for both Ranking Project types — no type branching here at all.
                        The backend already resolves everything type-specific (rank_name text, and
                        whether score applies) into this one common shape (see contracts/get-ranking-projects.md). */}
          {rankingProject.ranking_summary?.map((eachSummary) => (
            <li key={eachSummary.rank_name}>
              <label>{eachSummary.rank_name}</label>
              <span className="errorColor">
                {eachSummary.score ?? eachSummary.record_count}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

const RankingProjectCardList: React.FC<{}> = () => {
  const { projectDetails } = useProjectDetailsStore();
  const rankingProjectsLoader = useRankingProjectListStore(
    (state) => state.rankingProjectsLoader,
  );
  const dataFetchError = useRankingProjectListStore(
    (state) => state.dataFetchError,
  );
  const rankingProjects = useRankingProjectListStore(
    (state) => state.rankingProjects,
  );
  const refreshRankingProjectList = useRankingProjectListStore(
    (state) => state.refreshRankingProjectList,
  );

  useEffect(() => {
    if (projectDetails?.slug) {
      refreshRankingProjectList(projectDetails.slug).catch((error) =>
        console.log(error),
      );
    }
  }, [projectDetails?.slug, refreshRankingProjectList]);

  // Run Ranking's actual execution engine is a separate, not-yet-specified story (spec.md Out of Scope).
  // Read-only trigger (2026-07-27 follow-up): shows a notice only, no network call at all — matching
  // the same client-side-only pattern already used for Export Ranks/Copy/Delete/Create Ranking Project.
  const onRun = () => {
    message.info("Ranking execution engine is not yet implemented.");
  };

  return (
    <Spin spinning={rankingProjectsLoader}>
      <ul className="cardWrap rankingProjectCardWrap">
        {dataFetchError && (
          <CustomError
            errorDetails={dataFetchError}
            refreshAction={refreshRankingProjectList}
            refreshActionParams={projectDetails?.slug}
            reloadPage={false}
          />
        )}
        {!dataFetchError &&
          (rankingProjects?.length ? (
            rankingProjects.map((eachRankingProject) => (
              <RankingProjectCard
                key={eachRankingProject.id}
                rankingProject={eachRankingProject}
                onRun={onRun}
              />
            ))
          ) : (
            <li className="noDataBlk">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </li>
          ))}
      </ul>
    </Spin>
  );
};

export default RankingProjectCardList;
